import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { DashboardVehicleRow } from '../../types/dashboard';

interface Props {
  vehicles: DashboardVehicleRow[];
  loading?: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex items-center space-x-4 py-3 border-b">
        <div className="h-4 w-20 bg-gray-200 rounded"></div>
        <div className="h-4 w-12 bg-gray-200 rounded"></div>
        <div className="h-4 w-24 bg-gray-200 rounded"></div>
        <div className="h-4 w-20 bg-gray-200 rounded"></div>
        <div className="h-4 w-20 bg-gray-200 rounded"></div>
      </div>
    ))}
  </div>
);

export const VehicleQuickView: React.FC<Props> = ({ vehicles, loading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleRowClick = (vehicleId: number) => {
    navigate(`/vehicles`);
  };

  return (
    <div className="card p-4">
      <h3 className="text-lg font-semibold mb-4">{t('dashboard.topVehicles')}</h3>

      {loading ? (
        <LoadingSkeleton />
      ) : vehicles.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-gray-500">
          {t('common.noData')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b">
                <tr className="text-left text-gray-500">
                  <th className="pb-2 font-medium">{t('vehicles.licensePlate')}</th>
                  <th className="pb-2 font-medium text-center">{t('dashboard.tripCount')}</th>
                  <th className="pb-2 font-medium text-right">{t('dashboard.revenue')}</th>
                  <th className="pb-2 font-medium text-right">{t('dashboard.profit')}</th>
                  <th className="pb-2 font-medium text-right">{t('dashboard.lastTrip')}</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((vehicle) => {
                  const isIdle = !vehicle.lastTripDate;
                  return (
                    <tr
                      key={vehicle.vehicleId}
                      onClick={() => handleRowClick(vehicle.vehicleId)}
                      className={`border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors ${
                        isIdle ? 'bg-yellow-50' : ''
                      }`}
                    >
                      <td className="py-3 font-medium">{vehicle.licensePlate}</td>
                      <td className="py-3 text-center">{vehicle.tripCount}</td>
                      <td className="py-3 text-right">{formatCurrency(vehicle.totalRevenue)}đ</td>
                      <td
                        className={`py-3 text-right font-medium ${
                          vehicle.profit >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {formatCurrency(vehicle.profit)}đ
                        <span className="text-xs text-gray-400 ml-1">
                          ({vehicle.profitMargin.toFixed(0)}%)
                        </span>
                      </td>
                      <td className="py-3 text-right text-gray-500">
                        {vehicle.lastTripDate
                          ? format(parseISO(vehicle.lastTripDate), 'dd/MM/yyyy')
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
